import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { UserPlus, UserMinus, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API_URL as API } from '../config';

export function FollowButton({ userId, className }) {
    const { user } = useAuth();
    const [isFollowing, setIsFollowing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchStatus = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/follows/status/${userId}`);
            setIsFollowing(response.data.is_following);
        } catch (error) {
            console.error('Failed to fetch follow status:', error);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        if (user && userId && user.id !== userId) {
            fetchStatus();
        } else {
            setLoading(false);
        }
    }, [user, userId, fetchStatus]);

    const handleToggleFollow = async () => {
        if (!user) {
            toast.error('Please login to follow members');
            return;
        }

        setActionLoading(true);
        try {
            if (isFollowing) {
                await axios.delete(`${API}/follows/${userId}`);
                setIsFollowing(false);
                toast.success('Unfollowed user');
            } else {
                await axios.post(`${API}/follows/${userId}`);
                setIsFollowing(true);
                toast.success('Following user');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update follow status');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading || !user || user.id === userId) return null;

    return (
        <Button
            variant={isFollowing ? "secondary" : "outline"}
            size="sm"
            className={`${className} whitespace-nowrap ${isFollowing ? 'bg-zinc-800 text-zinc-300' : 'btn-secondary'}`}
            onClick={handleToggleFollow}
            disabled={actionLoading}
        >
            {isFollowing ? (
                <>
                    <Check className="w-4 h-4 mr-2" />
                    Following
                </>
            ) : (
                <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Follow
                </>
            )}
        </Button>
    );
}
